import { useMemo } from 'react';

import { ApplicationStatus } from '@/utils/constants';
import { Translations } from '@/utils/translations/Translations';
import { ActionCardDefinition } from '@/components/dashboard_components/applicant_dashboard/ActionCard/ActionCardDefinitions';

interface UseDashboardActionCardDefinitionsParams {
  translatedText: Translations;
  selectedProgramName: string;
  mainAppLatestCycleName: string | null | undefined;
  mainAppNextOpenDate: string | null | undefined;
  loading: boolean;
  portalOpenMain: boolean;
  portalOpenGrowth: boolean;
  portalOpenState: boolean;
  disabledGrowthAwardButton: boolean;
  hasMainApplication: boolean;
  hasGrowthApplication: boolean;
  hasStateApplication: boolean;
  mainStatusButtonText: string;
  growthStatusButtonText: string;
  stateStatusButtonText: string;
  mainApplicationStatus: ApplicationStatus | null | undefined;
  stateApplicationStatus: ApplicationStatus | null | undefined;
  onMainActionClick: () => void;
  onGrowthActionClick: () => void;
  onStateQuestionsActionClick: () => void;
}

interface UseDashboardActionCardDefinitionsResult {
  mainActionCard: ActionCardDefinition;
  growthAwardActionCard: ActionCardDefinition;
  stateQuestionsActionCard: ActionCardDefinition;
}

function useDashboardActionCardDefinitions({
  translatedText,
  selectedProgramName,
  mainAppLatestCycleName,
  mainAppNextOpenDate,
  loading,
  portalOpenMain,
  portalOpenGrowth,
  portalOpenState,
  disabledGrowthAwardButton,
  hasMainApplication,
  hasGrowthApplication,
  hasStateApplication,
  mainStatusButtonText,
  growthStatusButtonText,
  stateStatusButtonText,
  mainApplicationStatus,
  stateApplicationStatus,
  onMainActionClick,
  onGrowthActionClick,
  onStateQuestionsActionClick,
}: UseDashboardActionCardDefinitionsParams): UseDashboardActionCardDefinitionsResult {
  return useMemo(() => {
    const mainActionCard: ActionCardDefinition = {
      headerText:
        mainAppLatestCycleName != null
          ? `${mainAppLatestCycleName} ${selectedProgramName.toUpperCase()} ${translatedText.dashboard.labelApplication}`
          : '',
      buttonDisabled: loading || !portalOpenMain,
      buttonText: hasMainApplication
        ? mainStatusButtonText
        : translatedText.dashboard.labelCreateApplication,
      labelText:
        !portalOpenMain && mainAppLatestCycleName != null
          ? translatedText.dashboard.portalClosedInformation(
              mainAppLatestCycleName,
              mainAppNextOpenDate
            )
          : '',
      onClick: onMainActionClick,
    };

    const growthAwardActionCard: ActionCardDefinition = {
      headerText: translatedText.dashboard.growthAwardCardHeader,
      buttonDisabled: loading || disabledGrowthAwardButton || !portalOpenGrowth,
      buttonText: hasGrowthApplication
        ? growthStatusButtonText
        : translatedText.dashboard.growthAwardCardStartButton,
      labelText: disabledGrowthAwardButton
        ? translatedText.dashboard.growthAwardCardPendingLabel(selectedProgramName)
        : '',
      onClick: onGrowthActionClick,
    };

    const stateQuestionsActionCard: ActionCardDefinition = {
      headerText: translatedText.dashboard.stateQuestionsCardHeader,
      buttonDisabled: loading || !portalOpenState,
      buttonText: hasStateApplication
        ? stateStatusButtonText
        : translatedText.dashboard.stateQuestionsCardStartButton,
      labelText:
        mainApplicationStatus === ApplicationStatus.InProgress && stateApplicationStatus == null
          ? translatedText.dashboard.stateQuestionsCardPendingLabel
          : '',
      onClick: onStateQuestionsActionClick,
    };

    return {
      mainActionCard,
      growthAwardActionCard,
      stateQuestionsActionCard,
    };
  }, [
    translatedText,
    selectedProgramName,
    mainAppLatestCycleName,
    mainAppNextOpenDate,
    loading,
    portalOpenMain,
    portalOpenGrowth,
    portalOpenState,
    disabledGrowthAwardButton,
    hasMainApplication,
    hasGrowthApplication,
    hasStateApplication,
    mainStatusButtonText,
    growthStatusButtonText,
    stateStatusButtonText,
    mainApplicationStatus,
    stateApplicationStatus,
    onMainActionClick,
    onGrowthActionClick,
    onStateQuestionsActionClick,
  ]);
}

export default useDashboardActionCardDefinitions;
